import axios from '@/utils/axios';

export interface IDeviceIdConfiguration {
	sdkKey: string;
	enabled: boolean;
}

export interface IDocumentVerificationConfiguration {
	enabled: boolean;
}
export interface IAuthenticationPageConfiguration {
	forceMobilePhone: boolean;
	authenticationPageReminderText: string;
	showSmsOptInForManual: boolean;
	showSmsOptInForAuthenticated: boolean;
	smsOptInLabel: string;
}

export interface IVehicleCollateralPageConfiguration {
	useVehicleOptions: boolean;
}

export interface ISimplifiedLoanOfferPageConfiguration {
	simplifiedLoanOfferPageCardText: string;
	simplifiedLoanOfferPageDynamicHtml: string;
}
export interface IProductIneligiblePageConfiguration {
	productIneligiblePageCardText: string;
	productIneligiblePageDynamicHtml: string;
}

export interface IAnalyticsConfiguration {
	googleAnalyticsMeasurementId: string;
	crazyEggAccountNumber: string;
}

export interface IFeatureToggleConfiguration {
	enabled: boolean;
	type: string;
}

export interface ILoanPurposeQuestionConfigurations {
	loanPurposeQuestions: Array<ILoanPurposeQuestionConfiguration>;
}

export interface ILoanPurposeQuestionConfiguration {
	id: number;
	loanApplicationConfigurationId: number;
	displayText: string;
	displayOrder: number;
	enabled: boolean;
}

export interface ILoanPurposeQuestionPageConfiguration {
	loanPurposeQuestionHeader: string;
	includeBusinessPurposeOnLoanPurposeQuestion: boolean;
}

export interface IHeroConversationalTextConfiguration {
	id: number;
	loanTypeId: number | null;
	pageIdentifier: string;
	desktopHeroText: string;
	mobileHeroText: string;
	enabled: boolean;
}

export interface IHeroConversationalTextConfigurationsResponse {
	heroTextConfigurations: IHeroConversationalTextConfiguration[];
}

const getDeviceIdConfiguration = async (): Promise<IDeviceIdConfiguration> => {
	try {
		const { data: deviceConfiguration } = await axios.get<IDeviceIdConfiguration>(
			`${process.env.BASE_URL}api/Application/Configuration/DeviceId`
		);

		return deviceConfiguration;
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getDocumentVerificationConfiguration = async (): Promise<IDocumentVerificationConfiguration> => {
	try {
		const { data: documentVerificationConfiguration } = await axios.get<IDocumentVerificationConfiguration>(
			`${process.env.BASE_URL}api/Configuration/DocumentVerification/EnabledStatus`
		);

		return documentVerificationConfiguration;
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getAuthenticationPageConfiguration = async (): Promise<IAuthenticationPageConfiguration> => {
	try {
		const {
			data: { forceMobilePhone, authenticationPageReminderText, showSmsOptInForManual, showSmsOptInForAuthenticated, smsOptInLabel}
		} = await axios.get<IAuthenticationPageConfiguration>(
			`${process.env.BASE_URL}api/Configuration/Authentication`
		);

		return { forceMobilePhone, authenticationPageReminderText, showSmsOptInForManual, showSmsOptInForAuthenticated, smsOptInLabel };
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getVehicleCollateralPageConfiguration = async (): Promise<IVehicleCollateralPageConfiguration> => {
	try {
		const {
			data: { useVehicleOptions }
		} = await axios.get<IVehicleCollateralPageConfiguration>(
			`${process.env.BASE_URL}api/Configuration/VehicleCollateral`
		);

		return { useVehicleOptions };
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getSimplifiedLoanOfferPageConfig = async (): Promise<ISimplifiedLoanOfferPageConfiguration> => {
	try {
		const {
			data: { simplifiedLoanOfferPageCardText, simplifiedLoanOfferPageDynamicHtml }
		} = await axios.get<ISimplifiedLoanOfferPageConfiguration>(
			`${process.env.BASE_URL}api/Configuration/SimplifiedLoanOffer`
		);

		return { simplifiedLoanOfferPageCardText, simplifiedLoanOfferPageDynamicHtml };
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getProductIneligiblePageConfig = async (): Promise<IProductIneligiblePageConfiguration> => {
	try {
		const {
			data: { productIneligiblePageCardText, productIneligiblePageDynamicHtml }
		} = await axios.get<IProductIneligiblePageConfiguration>(
			`${process.env.BASE_URL}api/Configuration/ProductIneligible`
		);

		return { productIneligiblePageCardText, productIneligiblePageDynamicHtml };
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getFeatureToggleStatus = async (featureToggleId: number): Promise<boolean> => {
	try {
		const { data } = await axios.get(`${process.env.BASE_URL}api/Configuration/FeatureToggle/${featureToggleId}`);

		return data.featureToggle.enabled;
	} catch (error) {
		console.error(`Error getting feature toggle ${featureToggleId}:`, error);

		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getLoanPurposeQuestionConfigurations = async (loanApplicationConfigurationId: number): Promise<ILoanPurposeQuestionConfiguration> => {
	try {
		const { data } = await axios.get(`${process.env.BASE_URL}api/Configuration/LoanPurposeQuestions/${loanApplicationConfigurationId}`);

		return data.loanPurposeQuestions;
	} catch (error) {
		console.error(`Error getting loan purpose questions for loan app config id: ${loanApplicationConfigurationId}:`, error);

		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getLoanPurposeQuestionPageConfiguration = async (): Promise<ILoanPurposeQuestionPageConfiguration> => {
	try {
		const {
			data: { loanPurposeQuestionHeader, includeBusinessPurposeOnLoanPurposeQuestion }
		} = await axios.get<ILoanPurposeQuestionPageConfiguration>(
			`${process.env.BASE_URL}api/Configuration/Page/LoanPurposeQuestions`
		);

		return { loanPurposeQuestionHeader, includeBusinessPurposeOnLoanPurposeQuestion };
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

const getHeroConversationalTextConfigurations = async (): Promise<IHeroConversationalTextConfiguration[]> => {
	try {
		const {
			data: { heroTextConfigurations }
		} = await axios.get<IHeroConversationalTextConfigurationsResponse>(
			`${process.env.BASE_URL}api/Configuration/HeroConversationalText`
		);
		return heroTextConfigurations || [];
	} catch (error) {
		if (typeof error === 'string') {
			throw new Error(error);
		}

		throw error;
	}
};

export default {
	getDeviceIdConfiguration,
	getDocumentVerificationConfiguration,
	getAuthenticationPageConfiguration,
	getSimplifiedLoanOfferPageConfig,
	getProductIneligiblePageConfig,
	getVehicleCollateralPageConfiguration,
	getFeatureToggleStatus,
	getLoanPurposeQuestionConfigurations,
	getLoanPurposeQuestionPageConfiguration,
	getHeroConversationalTextConfigurations
};
export {
	getDeviceIdConfiguration,
	getDocumentVerificationConfiguration,
	getAuthenticationPageConfiguration,
	getSimplifiedLoanOfferPageConfig,
	getProductIneligiblePageConfig,
	getVehicleCollateralPageConfiguration,
	getFeatureToggleStatus,
	getLoanPurposeQuestionConfigurations,
	getLoanPurposeQuestionPageConfiguration,
	getHeroConversationalTextConfigurations
};
